import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Link,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import {
    BAR_CHART_TOOLTIP,
    CATEGORY_XAXIS_OPTIONS,
    COMMON_OPTIONS,
    FIELDS,
    LABELS,
    VERTICAL_YAXIS_OPTIONS,
} from './ComplianceCheck.constants';
import React, {
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { SCAN_STATUSES, TABS } from '/b2b/domains/constants';
import { emerald, grey, peach } from '/b2b/common/colors';
import { fetchDomainScans, fetchDomainSummaries } from '/b2b/domains/services';

import ActivityIcon from '/b2b/common/icons/Activity';
import AuthenticatedPaths from '/b2b/routing/AuthenticatedRoutes/AuthenticatedRoutes.paths';
import ComplianceCheckContext from './ComplianceCheck.context';
import { EChart } from '/b2b/common/components/Charts';
import Locale from '/b2b/common/components/Locale';
import PropTypes from 'prop-types';
import { SCAN_ID_PARAM_KEY } from '/b2b/domains/routes/DomainDetails/DomainDetails.hook';
import { makeStyles } from 'tss-react/mui';
import merge from 'lodash/merge';
import moment from 'moment';
import styles from './ComplianceCheck.styles';
import { translate } from '/b2b/common/helpers/i18n';
import { useTheme } from '@mui/material/styles';
import { withAsyncData } from '/b2b/common/hoc/withAsyncData';
import withRetranslate from '/b2b/common/hoc/withRetranslate';

const MAX_RECENT_SCANS = 6;

const staticOptions = merge({}, COMMON_OPTIONS, VERTICAL_YAXIS_OPTIONS, {
    xAxis: merge({}, COMMON_OPTIONS.xAxis, CATEGORY_XAXIS_OPTIONS.xAxis),
    yAxis: merge({}, COMMON_OPTIONS.yAxis, VERTICAL_YAXIS_OPTIONS.yAxis),
    tooltip: BAR_CHART_TOOLTIP,
});

const useStyles = makeStyles()(styles);

const getEarliestDomain = domains => {
    if (!domains || domains.length === 0) {
        return null;
    }
    const sorted = [...domains].sort((a, b) => {
        const dateA = new Date(a.domainCreated || 0);
        const dateB = new Date(b.domainCreated || 0);
        return dateA - dateB;
    });
    return sorted[0];
};

const ComplianceCheck = props => {
    const {
        customerId,
        loading: providedLoading,
        selectedDomain: providedDomain,
        shouldRetranslate,
        updateSelectedDomain,
    } = props;
    const { classes, cx } = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const { loading } = useContext(ComplianceCheckContext);

    const isLoading = loading || providedLoading;

    const echartsInstance = useRef(null);
    const [allDomains, setAllDomains] = useState([]);
    const [domainScans, setDomainScans] = useState([]);
    const [loadingScans, setLoadingScans] = useState(false);
    const [loadingDomains, setLoadingDomains] = useState(false);

    // Derive selected domain from Redux preference or earliest created
    const selectedDomain = useMemo(() => {
        if (!allDomains.length) {
            return null;
        }
        if (providedDomain) {
            const found = allDomains.find(d => d.domain === providedDomain);
            if (found) {
                return found;
            }
        }
        return getEarliestDomain(allDomains);
    }, [allDomains, providedDomain]);

    // Fetch all domains for dropdown; re-fetch when customer changes
    useLayoutEffect(() => {
        setLoadingDomains(true);
        fetchDomainSummaries({ pageSize: 100 })
            .then(response => {
                const domains = response?.results || [];
                const sortedDomains = [...domains].sort((a, b) => a.domain.localeCompare(b.domain));
                setAllDomains(sortedDomains);
            })
            .finally(() => {
                setLoadingDomains(false);
            });
    }, [customerId]);

    // Fetch scans when selected domain changes
    useEffect(() => {
        if (!selectedDomain) {
            return;
        }

        setLoadingScans(true);
        fetchDomainScans({
            domain: selectedDomain.domain,
            limit: 10,
            status: SCAN_STATUSES.COMPLETED,
        })
            .then(response => {
                const scans = response?.results || [];
                setDomainScans(scans);
            })
            .finally(() => {
                setLoadingScans(false);
            });
    }, [selectedDomain]);

    // Update selected domain
    const handleDomainChange = useCallback(
        (event, newValue) => {
            if (newValue) {
                updateSelectedDomain(newValue.domain);
            }
        },
        [updateSelectedDomain]
    );

    // Get latest scan for the selected domain
    const latestScan = useMemo(() => {
        if (!domainScans || domainScans.length === 0) {
            return null;
        }
        return domainScans[0]; // Scans are returned in descending order by date
    }, [domainScans]);

    // Configure stacked bar chart option with real scan data (dataset + series pattern from VerifiedRequests)
    const chartOption = useMemo(() => {
        if (!domainScans || domainScans.length === 0) {
            return null;
        }

        const formatPattern = moment.localeData().longDateFormat('lll');
        const formatPatternMinusYear = formatPattern
            .replace(/Y/g, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
        const recentScans = domainScans.slice(0, MAX_RECENT_SCANS).reverse();

        const reportData = recentScans.map(scan => ({
            date: moment(scan.completedAt).format(formatPatternMinusYear),
            [FIELDS.passed]: scan.passedChecksCount || 0,
            [FIELDS.failed]: scan.failedChecksCount || 0,
            [FIELDS.error]: scan.errorChecksCount || 0,
        }));

        const seriesColors = {
            [FIELDS.passed]: emerald,
            [FIELDS.failed]: peach,
            [FIELDS.error]: grey[400],
        };
        const seriesNameToField = Object.fromEntries(
            Object.entries(LABELS).map(([field, path]) => [translate(path), field])
        );

        return merge({}, staticOptions, {
            tooltip: {
                ...staticOptions.tooltip,
                formatter(params) {
                    if (params.length > 0) {
                        const date = params[0].axisValue;
                        let result = `<b>${date}</b><br/>`;
                        params.forEach(param => {
                            const field = seriesNameToField[param.seriesName];
                            const value = field != null ? param.data[field] : param.value;
                            result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
                        });
                        return result;
                    }
                    return '';
                },
            },
            grid: {
                left: parseInt(theme.spacing(5.5), 10),
                right: parseInt(theme.spacing(4.5), 10),
                bottom: '15%',
                top: '10%',
            },
            legend: {
                data: Object.values(LABELS).map(translate),
            },
            dataset: {
                source: reportData,
            },
            yAxis: merge({}, staticOptions.yAxis, {
                name: translate('domains.dashboard.yAxis.label'),
                nameGap: parseInt(theme.spacing(5.5), 10),
                show: reportData.length > 0,
            }),
            series: Object.entries(LABELS).map(([field, path]) => ({
                dimensions: [
                    { name: 'date', type: 'category' },
                    { name: field, type: 'int' },
                ],
                name: translate(path),
                stack: 'x',
                type: 'bar',
                itemStyle: { color: seriesColors[field] },
                label: { show: false },
            })),
        });
    }, [domainScans, shouldRetranslate, theme]);

    const onChartReady = useCallback(chart => {
        echartsInstance.current = chart;
    }, []);

    const handleChartClick = useCallback(
        params => {
            const recentScans = domainScans.slice(0, MAX_RECENT_SCANS).reverse();
            const clickedScan = recentScans[params.dataIndex];
            if (clickedScan && selectedDomain && clickedScan.id) {
                const urlParts = [
                    AuthenticatedPaths.COMPLIANCE_CHECK_SITES,
                    encodeURIComponent(selectedDomain.domain),
                    TABS.SCANS,
                ];
                history.push(`${urlParts.join('/')}?${SCAN_ID_PARAM_KEY}=${clickedScan.id}`);
            }
        },
        [domainScans, selectedDomain, history]
    );

    const chartEvents = useMemo(
        () => ({
            click: handleChartClick,
        }),
        [handleChartClick]
    );

    const hasNoDomains = !loadingDomains && allDomains.length === 0;
    const selectedDomainHasNoScans =
        !loadingDomains && allDomains.length > 0 && selectedDomain && domainScans.length === 0;
    const hasScans = domainScans.length > 0;

    return (
        <Paper className={classes.paper} elevation={0}>
            <Box className={classes.header}>
                <Box display="flex" alignItems="center">
                    <ActivityIcon className={classes.icon} fill={peach} />
                    <Typography variant="h4">
                        <Locale path="domains.complianceCheck.title" />
                    </Typography>
                </Box>
                {!loadingDomains && allDomains.length > 0 && (
                    <Link
                        className={classes.link}
                        component={RouterLink}
                        to={AuthenticatedPaths.COMPLIANCE_CHECK_SITES}
                    >
                        <Locale path="domains.viewAllDomains" />
                    </Link>
                )}
            </Box>

            {!loadingDomains && allDomains.length > 0 && (
                <Fragment>
                    <Typography
                        align="left"
                        className={classes.chartTitle}
                        gutterBottom
                        variant="h5"
                    >
                        <Locale path="common.requirement" options={{ smart_count: 0 }} />
                    </Typography>
                    <Box className={classes.domainSelectWrapper}>
                        <Autocomplete
                            className={classes.domainSelect}
                            disabled={loadingDomains || loadingScans}
                            fullWidth={false}
                            getOptionLabel={option => option.domain || ''}
                            loading={loadingDomains}
                            onChange={handleDomainChange}
                            options={allDomains}
                            value={selectedDomain}
                            renderInput={params => (
                                <TextField {...params} variant="outlined" size="medium" />
                            )}
                            sx={{
                                minWidth: 200,
                                width: `max(200px, ${(selectedDomain?.domain?.length ?? 20) + 10}ch)`,
                            }}
                        />
                    </Box>
                </Fragment>
            )}

            {loadingDomains || loadingScans ? (
                <Box className={cx(classes.chartCard, classes.loadingCard)}>
                    <CircularProgress />
                </Box>
            ) : hasNoDomains ? (
                <Box className={cx(classes.chartCard, classes.noScansCard)}>
                    <Typography variant="body1" className={classes.noScansText}>
                        <Locale path="domains.dashboard.noDomains" />
                    </Typography>
                    <Button
                        color="primary"
                        component={RouterLink}
                        to={AuthenticatedPaths.COMPLIANCE_CHECK_SITES}
                        variant="contained"
                    >
                        <Locale path="domains.dashboard.addDomain" />
                    </Button>
                </Box>
            ) : selectedDomainHasNoScans ? (
                <Box className={cx(classes.chartCard, classes.noScansCard)}>
                    <Typography variant="body1" className={classes.noScansText}>
                        <Locale path="domains.dashboard.noScansForDomain" />
                    </Typography>
                    <Button
                        color="primary"
                        component={RouterLink}
                        to={`${AuthenticatedPaths.COMPLIANCE_CHECK_SITES}/${encodeURIComponent(selectedDomain?.domain)}/${TABS.SCANS}`}
                        variant="contained"
                    >
                        <Locale path="domains.dashboard.addScan" />
                    </Button>
                </Box>
            ) : hasScans ? (
                <Fragment>
                    <Box className={classes.chartContainer}>
                        <Box className={classes.chart}>
                            <EChart
                                option={chartOption}
                                onChartReady={onChartReady}
                                onEvents={chartEvents}
                                loading={isLoading}
                                noResults={false}
                                sx={{ height: 300, width: '100%' }}
                            />
                        </Box>
                        <Box className={classes.chartFooter}>
                            <Typography variant="caption" className={classes.lastScanned}>
                                {latestScan?.completedAt && (
                                    <Locale
                                        path="domains.lastScannedOn"
                                        options={{
                                            scanDate: moment(latestScan.completedAt).format('LLL'),
                                        }}
                                    />
                                )}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                component={RouterLink}
                                to={`${AuthenticatedPaths.COMPLIANCE_CHECK_SITES}/${encodeURIComponent(selectedDomain?.domain)}/${TABS.SCANS}`}
                            >
                                <Locale path="common.viewDetails" />
                            </Button>
                        </Box>
                    </Box>
                </Fragment>
            ) : null}
        </Paper>
    );
};

ComplianceCheck.propTypes = {
    customerId: PropTypes.string,
    data: PropTypes.object,
    loading: PropTypes.bool,
    selectedDomain: PropTypes.string,
    shouldRetranslate: PropTypes.string,
    updateSelectedDomain: PropTypes.func.isRequired,
};

export default withAsyncData(withRetranslate(ComplianceCheck), ComplianceCheckContext);
